import { useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreate,
  useNotify,
  SimpleForm,
  Create,
  useResourceContext,
  required,
  maxLength,
  useRefresh,
  minLength,
  TextInput,
  useTranslate,
  useGetIdentity,
  RecordContextProvider,
} from "react-admin";
import MuiDatePickerInput from "../../common/MuiDatePickerInput";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";
import RadioGroupX from "../common/RadioGroupX";
import Box from "@mui/material/Box";
import currentDate from "../../../utils/currentDate";
import DeliveryNote from "../common/DeliveryNote";
import MutationInventoryWriteOffItemForm from "./components/MutationInventoryWriteOffItemForm";
import TotalInput from "../../saleOrders/common/TotalInput";
import CodeNumberInput from "../../common/CodeNumberInput";

const validateNumber = [required(), minLength(12), maxLength(12)];
const validateDate = [required(), minLength(10), maxLength(10)];
const validateNote = [required()];

export default function MutationInventoryWriteOffAddDialog({ open, setOpen }) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const resource = useResourceContext(); // cuman nama resource
  const t = useTranslate();
  const { identity, isLoading: identityLoading } = useGetIdentity();

  const save = useCallback(
    async (values) => {
      try {
        await create(
          resource,
          { data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.created", {
                messageArgs: { smart_count: 1 },
              });
              setOpen(false);
              refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [create]
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted={false}
      fullWidth={true}
      maxWidth={false}
    >
      <Create title={" "} sx={{ "& .RaCreate-main": { mt: 0 } }}>
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>
              {t("mine.dialog.create_mutation_inventory_write_off")}
            </DialogTitle>
            <IconButton onClick={handleClose} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent sx={{ alignSelf: "normal" }}>
            <CodeNumberInput
              open={open}
              dependOn="date"
              variant="outlined"
              source="code"
              label="mine.label.receipt_no"
              fullWidth
              validate={validateNumber}
            />
            <MuiDatePickerInput
              variant="outlined"
              source="date"
              label="mine.label.date"
              fullWidth
              validate={validateDate}
              defaultValue={currentDate()}
            />
            {identityLoading ? null : (
              <RecordContextProvider value={identity}>
                <TextInput
                  variant="outlined"
                  source="fullName"
                  label="mine.label.operator_name"
                  fullWidth
                  disabled
                />
              </RecordContextProvider>
            )}
            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              fullWidth
              multiline
              rows={3}
              validate={validateNote}
            />
            <RadioGroupX
              sx={{
                "& .MuiFormGroup-root": { justifyContent: "space-around" },
              }}
              choices={[
                { id: 1, name: t("mine.label.stocktake") },
                { id: 2, name: t("mine.label.sample") },
                { id: 3, name: t("mine.label.warehouse_broke") },
                { id: 4, name: t("mine.label.transfer_between_depo") },
              ]}
            />
            <DeliveryNote />
            <Box
              sx={{
                maxHeight: '300px', // Sesuaikan tinggi maksimum sesuai kebutuhan Anda
                overflowY: 'auto',
                border: '1px solid #ccc', // Opsional: untuk visualisasi batas
                borderRadius: '4px',
                marginBottom: '24px',
                p: 1, // Opsional: padding
              }}
            >
              <MutationInventoryWriteOffItemForm />
            </Box>

            <TotalInput helperText={t("mine.label.according_to_cogs")} />
          </DialogContent>
        </SimpleForm>
      </Create>
    </Dialog>
  );
}
